import { useEffect, useMemo, useState } from "react";
import { speakUpClient } from "../apiClient/speakUpClient";
import { ApiError } from "../../platform/apiClient/apiRequest";
import { EyeOffIcon, UserIcon } from "../components/icons";
import type { SpeakUpMessage } from "../types/speakUp.types";

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" });
}

function formatRelative(iso: string): string {
  const minutes = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return formatDate(iso);
}

function AttributionBadge({ item }: { item: SpeakUpMessage }) {
  return item.isAnonymous ? (
    <span className="speakup-badge speakup-badge--anon">
      <EyeOffIcon />
      Anonymous
    </span>
  ) : (
    <span className="speakup-badge speakup-badge--named">
      <UserIcon />
      {item.submitterEmail}
    </span>
  );
}

function SpeakUpCard({ item, onChanged }: { item: SpeakUpMessage; onChanged: () => void }) {
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [isReplying, setIsReplying] = useState(false);
  const [replyDraft, setReplyDraft] = useState(item.adminReply ?? "");
  const [savingReply, setSavingReply] = useState(false);
  const [replyError, setReplyError] = useState<string | null>(null);

  async function handleDelete() {
    setSubmitting(true);
    setError(null);
    try {
      await speakUpClient.deleteMessage(item.id);
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't delete this.");
      setSubmitting(false);
      setConfirmingDelete(false);
    }
  }

  function startReplying() {
    setReplyDraft(item.adminReply ?? "");
    setReplyError(null);
    setIsReplying(true);
  }

  async function handleSaveReply() {
    const trimmed = replyDraft.trim();
    if (!trimmed) {
      setReplyError("Write a reply first.");
      return;
    }
    setSavingReply(true);
    setReplyError(null);
    try {
      await speakUpClient.reply(item.id, trimmed);
      setIsReplying(false);
      onChanged();
    } catch (err) {
      setReplyError(err instanceof ApiError ? err.message : "Couldn't save this reply.");
    } finally {
      setSavingReply(false);
    }
  }

  return (
    <li className="speakup-card">
      <div className="speakup-card-top">
        <span className="speakup-number">#{item.referenceNumber}</span>
        <AttributionBadge item={item} />
        <span className="speakup-time" title={formatDate(item.createdAt)}>
          {formatRelative(item.createdAt)}
        </span>
      </div>
      <p className="speakup-message">{item.message}</p>

      {item.adminReply && !isReplying && (
        <div className="speakup-reply">
          <p className="speakup-reply-label">Admin reply</p>
          <p className="speakup-reply-text">{item.adminReply}</p>
          {item.repliedAt && <p className="speakup-reply-meta">Replied {formatDate(item.repliedAt)}</p>}
        </div>
      )}

      {isReplying && (
        <div className="speakup-reply-form">
          <textarea
            className="speakup-textarea"
            rows={4}
            maxLength={4000}
            placeholder={`Reply to #${item.referenceNumber}…`}
            value={replyDraft}
            onChange={(e) => setReplyDraft(e.target.value)}
          />
          {replyError && <span className="field-error">{replyError}</span>}
          <div className="speakup-card-actions">
            <button type="button" className="btn btn-ghost" onClick={() => setIsReplying(false)} disabled={savingReply}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary" onClick={handleSaveReply} disabled={savingReply}>
              {savingReply ? "Saving…" : "Save reply"}
            </button>
          </div>
        </div>
      )}

      <div className="speakup-card-actions">
        {!isReplying && (
          <button type="button" className="btn btn-ghost" onClick={startReplying}>
            {item.adminReply ? "Edit reply" : "Reply"}
          </button>
        )}
        {confirmingDelete ? (
          <div className="cancel-confirm">
            <span>Delete?</span>
            <button type="button" className="btn btn-ghost" onClick={() => setConfirmingDelete(false)} disabled={submitting}>
              No
            </button>
            <button type="button" className="btn btn-danger" onClick={handleDelete} disabled={submitting}>
              {submitting ? "Deleting…" : "Yes"}
            </button>
          </div>
        ) : (
          <button type="button" className="btn btn-danger-ghost" onClick={() => setConfirmingDelete(true)} disabled={submitting}>
            Delete
          </button>
        )}
      </div>
      {error && <span className="field-error">{error}</span>}
    </li>
  );
}

export function SpeakUpAdminPage() {
  const [messages, setMessages] = useState<SpeakUpMessage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  function refresh() {
    setIsLoading(true);
    setError(null);
    speakUpClient
      .getAdminMessages()
      .then(setMessages)
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load submissions."))
      .finally(() => setIsLoading(false));
  }

  useEffect(refresh, []);

  const anonymousCount = useMemo(() => messages.filter((m) => m.isAnonymous).length, [messages]);

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1>Speak Up submissions</h1>
          <p className="page-sub">
            Feedback from employees, newest first. Anonymous entries hide the sender here, but can still be traced back if one is
            reported as inappropriate.
          </p>
          {!isLoading && !error && messages.length > 0 && (
            <p className="speakup-stats">
              {messages.length} submission{messages.length === 1 ? "" : "s"} · {anonymousCount} anonymous
            </p>
          )}
        </div>
      </header>

      {error ? (
        <div className="state-panel">
          <p>{error}</p>
        </div>
      ) : isLoading ? (
        <div className="state-panel">Loading…</div>
      ) : messages.length === 0 ? (
        <div className="state-panel">
          <p>No submissions yet.</p>
        </div>
      ) : (
        <ul className="speakup-list">
          {messages.map((m) => (
            <SpeakUpCard key={m.id} item={m} onChanged={refresh} />
          ))}
        </ul>
      )}
    </div>
  );
}
